import React, { useState, useEffect } from "react";
import {
  MdClose,
  MdSearch,
  MdPerson,
  MdLink,
  MdLinkOff,
  MdBusiness
} from "react-icons/md";
import {
  getBusinessUsers,
  linkUserToCompany,
  unlinkUserFromCompany,
  Company
} from "../../utils/api/endpoints/companies";

interface ManageUsersModalProps {
  company: Company;
  onClose: () => void;
  onSuccess: () => void;
}

const ManageUsersModal: React.FC<ManageUsersModalProps> = ({
  company,
  onClose,
  onSuccess
}) => {
  const [businessUsers, setBusinessUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const fetchBusinessUsers = async () => {
    setLoading(true);
    const users = await getBusinessUsers(searchQuery);
    if (users) {
      setBusinessUsers(users);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBusinessUsers();
  }, [searchQuery]);

  const handleLinkUser = async (userId: number) => {
    setActionLoading(userId);
    const success = await linkUserToCompany(company.company_id, userId);
    if (success) {
      await fetchBusinessUsers();
      onSuccess();
    }
    setActionLoading(null);
  };

  const handleUnlinkUser = async (userId: number) => {
    setActionLoading(userId);
    const success = await unlinkUserFromCompany(userId);
    if (success) {
      await fetchBusinessUsers();
      onSuccess();
    }
    setActionLoading(null);
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Business Users
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Link or unlink users for: <span className="font-medium">{company.company_name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>

        {/* Search */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by username or email..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          </div>
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
            </div>
          ) : businessUsers.length === 0 ? (
            <div className="py-12 text-center">
              <MdPerson className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                {searchQuery
                  ? "No business users found matching your search"
                  : "No business users found"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {businessUsers.map((user) => {
                const isLinkedToThisCompany =
                  user.company_id === company.company_id;
                const isLinkedToOtherCompany =
                  user.company_id && user.company_id !== company.company_id;
                const isLoading = actionLoading === user.user_id;

                return (
                  <div
                    key={user.user_id}
                    className={`flex items-center justify-between rounded-lg border p-4 ${
                      isLinkedToThisCompany
                        ? "border-blue-200 bg-blue-50"
                        : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-600">
                        <MdPerson className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {user.username}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                        {user.Company && (
                          <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                            <MdBusiness className="h-3 w-3" />
                            <span>
                              {isLinkedToThisCompany
                                ? "Linked to this company"
                                : `Linked to: ${user.Company.company_name}`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      {isLinkedToThisCompany ? (
                        <button
                          onClick={() => handleUnlinkUser(user.user_id)}
                          disabled={isLoading}
                          className="flex items-center gap-2 rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isLoading ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent"></div>
                          ) : (
                            <>
                              <MdLinkOff className="h-4 w-4" />
                              Unlink
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleLinkUser(user.user_id)}
                          disabled={isLoading || isLinkedToOtherCompany}
                          className="flex items-center gap-2 rounded-lg border border-blue-300 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                          title={
                            isLinkedToOtherCompany
                              ? "User is already linked to another company"
                              : "Link user to this company"
                          }
                        >
                          {isLoading ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                          ) : (
                            <>
                              <MdLink className="h-4 w-4" />
                              Link
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {businessUsers.filter((u) => u.company_id === company.company_id)
                .length}{" "}
              user(s) linked to this company
            </p>
            <button
              onClick={onClose}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageUsersModal;

